import { useEffect, useState, useCallback } from 'react'
import {
  listAdminUsers, createAdminUser, patchAdminUser,
  deleteAdminUser, restoreAdminUser,
} from '../lib/api.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import useMe from '../hooks/useMe.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { BTN_PRIMARY, INPUT } from '../lib/ui.js'
import { IANA_TIME_ZONES, isValidTimeZone, timeZoneErrorMessage } from '../lib/datetime.js'

const ROLES = ['VIEWER', 'SPEAKER', 'ADMIN']

const ROLE_BADGE = {
  ADMIN: 'bg-brand-100 text-brand-800',
  SPEAKER: 'bg-slate-100 text-slate-700',
  VIEWER: 'bg-slate-100 text-slate-500',
}


function emptyNewUser() {
  return { userId: '', email: '', displayName: '', role: 'SPEAKER', tzId: '' }
}

// #379: tzId is optional here, so blank is fine — anything else has to be a
// zone the server will accept, checked before the request rather than after
// the 400. Returns the error sentence, or '' when the value is good.
export function tzCellError(value) {
  const tz = (value || '').trim()
  if (!tz) return ''
  return isValidTimeZone(tz) ? '' : timeZoneErrorMessage(tz)
}

export default function AdminUsersPage() {
  const { me } = useMe()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newUser, setNewUser] = useState(emptyNewUser)
  // userId -> message, for the TZ cell that was just rejected client-side.
  const [tzErrors, setTzErrors] = useState({})

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listAdminUsers({ includeDeleted })
      .then(setRows)
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [includeDeleted])

  useEffect(() => { load() }, [load])

  const updateField = async (userId, patch) => {
    try {
      const updated = await patchAdminUser(userId, patch)
      setRows((prev) => prev.map((r) => (r.userId === userId ? updated : r)))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const remove = async (userId, email) => {
    if (!window.confirm(`Soft-remove ${email}? They lose access immediately.`)) return
    try {
      await deleteAdminUser(userId)
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const restore = async (userId) => {
    try {
      const restored = await restoreAdminUser(userId)
      setRows((prev) => prev.map((r) => (r.userId === userId ? restored : r)))
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const submitNew = async (e) => {
    e.preventDefault()
    setError(null)
    try {
      const payload = {
        userId: Number(newUser.userId),
        email: newUser.email.trim(),
        role: newUser.role,
        displayName: newUser.displayName.trim() || null,
        tzId: newUser.tzId.trim() || null,
      }
      if (!payload.userId || !payload.email) {
        setError('userId and email are required.')
        return
      }
      const tzProblem = tzCellError(newUser.tzId)
      if (tzProblem) {
        setError(tzProblem)
        return
      }
      await createAdminUser(payload)
      setNewUser(emptyNewUser())
      setAdding(false)
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">Users</span>
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Members of the <code>actianlabs-next</code> Workbench group, plus their roles.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeDeleted}
                   onChange={(e) => setIncludeDeleted(e.target.checked)} />
            <span className="text-slate-700">Show removed</span>
          </label>
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className={BTN_PRIMARY}
          >
            {adding ? 'Cancel' : 'Add user'}
          </button>
        </div>
      </header>

      {error ? <ErrorAlert title="User management error" message={error} source="AdminUsersPage" /> : null}

      {/* #379: shared by the New-user form and every row's TZ cell, so the
          valid value is offered rather than guessed at. */}
      <datalist id="iana-tz-list">
        {IANA_TIME_ZONES.map((tz) => <option key={tz} value={tz} />)}
      </datalist>

      {adding ? (
        <form onSubmit={submitNew} className="mb-4 flex flex-wrap gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/60">
          <label className="text-sm">
            <span className="block text-xs font-medium text-slate-600">Workbench user_id</span>
            <input value={newUser.userId} onChange={(e) => setNewUser({ ...newUser, userId: e.target.value })}
                   className={`mt-1 ${INPUT}`} placeholder="e.g. 4242" required />
          </label>
          <label className="text-sm flex-1 min-w-[200px]">
            <span className="block text-xs font-medium text-slate-600">Email</span>
            <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                   className={`mt-1 w-full ${INPUT}`} required />
          </label>
          <label className="text-sm flex-1 min-w-[180px]">
            <span className="block text-xs font-medium text-slate-600">Display name</span>
            <input value={newUser.displayName} onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                   className={`mt-1 w-full ${INPUT}`} />
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-slate-600">Role</span>
            <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className={`mt-1 ${INPUT}`}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-xs font-medium text-slate-600">tzId (IANA, optional)</span>
            <input value={newUser.tzId} onChange={(e) => setNewUser({ ...newUser, tzId: e.target.value })}
                   className={`mt-1 ${INPUT}`} placeholder="America/New_York" list="iana-tz-list" />
          </label>
          <div className="flex items-end">
            <button type="submit"
                    className={BTN_PRIMARY}>
              Add
            </button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No members yet</h3>
        </div>
      ) : (
        /* #356: `overflow-hidden` CLIPPED the table at narrow widths, so the
           right-hand columns — which is where the actions live — were simply
           unreachable on a phone. `overflow-x-auto` plus an explicit min-width
           makes the table scroll inside its own card instead, which also keeps
           the page itself from scrolling sideways. */
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60">
          <table className="min-w-[720px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Display name</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">TZ</th>
                <th className="px-4 py-2">Joined</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((u) => {
                const isSelf = me?.userId === u.userId
                const isDeleted = !!u.deletedAt
                return (
                  <tr key={u.userId} className={isDeleted ? 'opacity-60' : ''}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900">{u.email}</div>
                      <div className="text-xs text-slate-500">user_id={u.userId}{isSelf ? ' (you)' : ''}</div>
                      {u.promotionRequestedAt && u.role === 'VIEWER' ? (
                        <div className="mt-1">
                          <span
                            className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                            title={`Requested speaker access at ${new Date(u.promotionRequestedAt).toLocaleString()} — promote to SPEAKER to clear.`}
                          >
                            Requested speaker on {new Date(u.promotionRequestedAt).toLocaleDateString()}
                          </span>
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <input
                        type="text"
                        defaultValue={u.displayName || ''}
                        onBlur={(e) => {
                          const value = e.target.value.trim()
                          if (value !== (u.displayName || '')) updateField(u.userId, { displayName: value })
                        }}
                        className={INPUT + ' w-44'}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={u.role || 'SPEAKER'}
                        onChange={(e) => updateField(u.userId, { role: e.target.value })}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-700'}`}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <input
                        type="text"
                        defaultValue={u.tzId || ''}
                        list="iana-tz-list"
                        aria-invalid={tzErrors[u.userId] ? 'true' : undefined}
                        onBlur={(e) => {
                          const value = e.target.value.trim()
                          const problem = tzCellError(value)
                          setTzErrors((prev) => {
                            const next = { ...prev }
                            if (problem) next[u.userId] = problem
                            else delete next[u.userId]
                            return next
                          })
                          // #379: a bad zone never leaves the browser.
                          if (problem) return
                          if (value !== (u.tzId || '')) updateField(u.userId, { tzId: value })
                        }}
                        className={
                          (tzErrors[u.userId]
                            ? INPUT.replace('border-slate-300', 'border-rose-400')
                            : INPUT) + ' w-40'
                        }
                        placeholder="—"
                      />
                      {tzErrors[u.userId] ? (
                        <p className="mt-1 max-w-[16rem] text-xs text-rose-600">{tzErrors[u.userId]}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 align-top text-xs text-slate-500">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      {isDeleted ? (
                        <button type="button" onClick={() => restore(u.userId)}
                                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
                          Restore
                        </button>
                      ) : (
                        <button type="button"
                                onClick={() => remove(u.userId, u.email)}
                                disabled={isSelf}
                                title={isSelf ? "You can't remove yourself." : ''}
                                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
